import React, {
    createContext,
    type FC,
    type PropsWithChildren,
    use,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import type { GQL } from "@binale-tech/shared";
import pLimit from "p-limit";
import { useApolloClient } from "@apollo/client/react";

import { CompanyContext } from "@domain/context/CompanyContext";
import { type GenericRecord } from "@domain/models/GenericRecord";
import { RecordsContext } from "@domain/context/accountingData/RecordsCtx";
import { getGenericRecordsLookup } from "@domain/context/queries/ctxQueries.graphql";
import { useFetchFirebaseRecordByPath } from "@domain/context/accountingData/hooks/useFetchFirebaseRecordByPath";

type SaldoSourcesCtx = {
    map: Map<string, GenericRecord>;
    saldoSourceIdsSet: Set<string>;
    isLoaded: boolean;
};

const FETCH_CONCURRENCY = 8;

export const SaldoSourcesContext = createContext<SaldoSourcesCtx>({
    map: new Map(),
    saldoSourceIdsSet: new Set(),
    isLoaded: true,
});

export const useSaldoSources = () => use(SaldoSourcesContext);

export const SaldoSourcesProvider: FC<PropsWithChildren> = (props) => {
    const { companyGQL } = use(CompanyContext);
    const { allRecords } = use(RecordsContext);
    const fetchFirebaseRecord = useFetchFirebaseRecordByPath();
    const client = useApolloClient();

    // cacheRef.get(id):
    //   undefined → never attempted
    //   null      → attempted, not found
    //   record    → attempted, found
    //
    // Both refs are *replaced* (not mutated) on company switch - in-flight resolutions
    // compare `cacheRef.current === ownCache` to detect "is this still my company?"
    // and skip stale writes without needing a generation counter.
    const cacheRef = useRef(new Map<string, GenericRecord | null>());
    const pendingRef = useRef(new Set<string>());

    const [map, setMap] = useState<Map<string, GenericRecord>>(new Map());
    const [pendingCount, setPendingCount] = useState(0);

    const companyId = companyGQL?.id;

    useEffect(() => {
        cacheRef.current = new Map();
        pendingRef.current = new Set();
        setMap(new Map());
        setPendingCount(0);
    }, [companyId]);

    // Resolve a batch of saldo source records by id:
    //  1. ONE GraphQL `genericRecordsLookup` call → resolves {productKey, year,
    //     groupId} for every id at once.
    //  2. Parallel Firebase `get()`s (capped via pLimit) at each resolved path.
    // Records can live in different (productKey, year, groupId) buckets, so the
    // Firebase reads can't be collapsed - but the SQL lookup absolutely can,
    // and previously wasn't.
    const fetchSaldoSourceRecords = useCallback(
        async (recordIds: readonly string[], cid: string): Promise<Map<string, GenericRecord | null>> => {
            const out = new Map<string, GenericRecord | null>();
            if (!recordIds.length) {
                return out;
            }
            let lookups: GQL.IRecordLookup[] = [];
            try {
                const { data } = await client.query({
                    query: getGenericRecordsLookup,
                    variables: { input: { companyId: cid, ids: [...recordIds] } },
                    fetchPolicy: "no-cache",
                });
                lookups = data?.genericRecordsLookup ?? [];
            } catch {
                // GraphQL hard failure → none of the saldo sources resolved.
                recordIds.forEach((id) => out.set(id, null));
                return out;
            }
            const lookupById = new Map(lookups.map((l) => [l.recordId, l]));

            const limit = pLimit(FETCH_CONCURRENCY);
            await Promise.all(
                recordIds.map((id) =>
                    limit(async () => {
                        const lookup = lookupById.get(id);
                        if (!lookup?.productKey || lookup.year == null) {
                            out.set(id, null);
                            return;
                        }
                        const record = await fetchFirebaseRecord({
                            productKey: lookup.productKey,
                            companyId: cid,
                            groupId: lookup.groupId ?? undefined,
                            year: lookup.year,
                            recordId: lookup.recordId,
                        });
                        out.set(id, record);
                    }),
                ),
            );
            return out;
        },
        [client, fetchFirebaseRecord],
    );

    const saldoSourceIdsSet = useMemo(() => {
        return new Set(allRecords.list.map((v) => v.saldoSourceId).filter(Boolean));
    }, [allRecords.list]);

    useEffect(() => {
        if (!companyId) {
            return;
        }

        const ownCache = cacheRef.current;
        const ownPending = pendingRef.current;

        const outOfWindow = [...saldoSourceIdsSet].filter((recordId) => !allRecords.map.has(recordId));
        if (!outOfWindow.length) {
            return;
        }

        const toFetch = outOfWindow.filter((recordId) => !ownCache.has(recordId) && !ownPending.has(recordId));

        const publish = () => {
            if (cacheRef.current !== ownCache) {
                return;
            }
            const next = new Map<string, GenericRecord>();
            ownCache.forEach((record, id) => {
                if (record) {
                    next.set(id, record);
                }
            });
            setMap(next);
            setPendingCount(ownPending.size);
        };

        if (!toFetch.length) {
            publish();
            return;
        }

        toFetch.forEach((recordId) => ownPending.add(recordId));
        setPendingCount(ownPending.size);

        fetchSaldoSourceRecords(toFetch, companyId).then((results) => {
            if (cacheRef.current !== ownCache) {
                return;
            }
            results.forEach((record, recordId) => {
                ownCache.set(recordId, record);
                ownPending.delete(recordId);
            });
            publish();
        });
    }, [companyId, saldoSourceIdsSet, allRecords.map, fetchSaldoSourceRecords]);

    const value = useMemo(
        () => ({ map, saldoSourceIdsSet, isLoaded: pendingCount === 0 }) satisfies SaldoSourcesCtx,
        [map, saldoSourceIdsSet, pendingCount],
    );

    return <SaldoSourcesContext value={value}>{props.children}</SaldoSourcesContext>;
};
